/**
 * Home Page - REFACTORED with Unified Content System
 * Landing page with hero section and featured work
 */

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiGithub, FiLinkedin, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import useEditableContent from '../hooks/useEditableContent';
import useCarousel from '../hooks/useCarousel';
import EditableModal from '../components/EditableModal';
import BlogEditModal from '../components/BlogEditModal';
import RichTextEditor from '../components/RichTextEditor';
import ContentCard from '../components/ContentCard';
import ContentGrid from '../components/ContentGrid';
import ConfirmModal from '../components/ConfirmModal';
import WeatherWidget from '../components/WeatherWidget';
import NewsTicker from '../components/NewsTicker';
import SEO from '../components/SEO';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const Home = () => {
  const { isAdmin } = useAuth();
  const { showToast } = useToast();

  // Use unified hook for hero content
  const {
    items: heroItems,
    loading: heroLoading,
    error: heroError,
    showModal: showHeroModal,
    formData: heroFormData,
    updateFormData: updateHeroFormData,
    startEdit: startHeroEdit,
    cancelEdit: cancelHeroEdit,
    save: saveHero,
    fetchItems: fetchHeroItems
  } = useEditableContent('hero', { page: 'home' });

  // Use unified hook for blog posts on home page - only published and highlighted
  const {
    items: blogPosts,
    loading: _blogLoading,
    error: _blogError,
    showModal: showBlogModal,
    isCreating: isCreatingBlog,
    formData: blogFormData,
    updateFormData: updateBlogFormData,
    startEdit: startBlogEdit,
    cancelEdit: cancelBlogEdit,
    save: saveBlog,
    deleteItem: deleteBlogItem,
    confirmDelete: confirmBlogDelete,
    confirmOpen: blogConfirmOpen,
    itemToDelete: blogItemToDelete,
    setConfirmOpen: setBlogConfirmOpen,
    fetchItems: fetchBlogItems
  } = useEditableContent('blog', { page: 'home', section: 'blog', published: true });

  const hero = heroItems[0] || {};

  // Toggle publish status
  const handleTogglePublish = async (post) => {
    try {
      const updatedMetadata = {
        ...post.metadata,
        published: post.metadata?.published === false ? true : false
      };
      
      await api.put(`/content-items/${post._id}`, {
        ...post,
        metadata: updatedMetadata
      });
      
      // Refresh the list to show updated status
      await fetchBlogItems();
      
      showToast(
        updatedMetadata.published ? 'Post published' : 'Post set as draft',
        'success'
      );
      return true;
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
      showToast('Failed to update publish status', 'error');
      return false;
    }
  };

  // Toggle featured/highlighted status
  const handleToggleHighlight = async (post) => {
    try {
      const isCurrentlyHighlighted = post.metadata?.page === 'home' && post.metadata?.section === 'blog';
      const updatedMetadata = {
        ...post.metadata,
        page: isCurrentlyHighlighted ? 'blog' : 'home',
        section: isCurrentlyHighlighted ? 'posts' : 'blog'
      };
      
      await api.put(`/content-items/${post._id}`, {
        ...post,
        metadata: updatedMetadata
      });
      
      // Refresh the list to show updated status
      await fetchBlogItems();
      
      showToast(
        isCurrentlyHighlighted ? 'Removed from featured' : 'Added to featured',
        'success'
      );
      return true;
    } catch (error) {
      console.error('Failed to toggle highlight status:', error);
      showToast('Failed to update featured status', 'error');
      return false;
    }
  };

  // Use carousel hook for blog posts
  const { 
    visibleItems: visibleBlogPosts, 
    isAnimating,
    shouldRotate,
    currentIndex 
  } = useCarousel(blogPosts, {
    visibleCount: 6,
    rotateCount: 3,
    interval: 8000,
    enabled: true
  });

  if (heroLoading) {
    return (
      <HomeContainer>
        <Container>
          <LoadingText>Loading...</LoadingText>
        </Container>
      </HomeContainer>
    );
  }

  // Show error state if hero failed to load
  if (heroError && heroItems.length === 0) {
    return (
      <HomeContainer>
        <Container>
          <ErrorMessage>
            <ErrorTitle>Failed to load content</ErrorTitle>
            <ErrorText>{heroError}</ErrorText>
            <RetryButton onClick={fetchHeroItems}>Retry</RetryButton>
          </ErrorMessage>
        </Container>
      </HomeContainer>
    );
  }

  return (
    <>
      <SEO
        title="Home"
        description="Professional portfolio of Richard Plotka - Professor, Innovator & Technology Leader. Explore projects, research, and teaching materials."
        keywords={['Richard Plotka', 'Portfolio', 'Professor', 'Technology', 'Innovation']}
      />
      <HomeContainer>
        {/* Weather Widget - Upper Right */}
        <WeatherWidgetPosition>
          <WeatherWidget />
        </WeatherWidgetPosition>

        {/* Hero Section */}
        <HeroSection>
        <Container>
          <HeroGrid>
            <HeroContent
              as={motion.div}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {isAdmin && (
                <EditButton onClick={() => startHeroEdit(hero)}>
                  <FiEdit2 /> Edit Hero
                </EditButton>
              )}
               
              <Greeting>{hero.data?.greeting || "Hello, I'm"}</Greeting>
              <Name>{hero.title || 'Richard Plotka'}</Name>
              <Title>{hero.subtitle || 'Full-Stack Developer & Architect'}</Title>
              <Description dangerouslySetInnerHTML={{ __html: hero.description || 'Transforming ideas into elegant, scalable solutions.' }} />
              
              <ButtonGroup>
                <PrimaryButton as={Link} to={hero.data?.cta1Link || '/portfolio'}>
                  {hero.data?.cta1Text || 'View My Work'} <FiArrowRight />
                </PrimaryButton>
                <SecondaryButton as={Link} to={hero.data?.cta2Link || '/contact'}>
                  {hero.data?.cta2Text || 'Get In Touch'}
                </SecondaryButton>
              </ButtonGroup>
              
              <SocialIcons>
                <SocialIcon href="https://github.com/rplotka" target="_blank" rel="noopener noreferrer">
                  <FiGithub size={24} />
                </SocialIcon>
                <SocialIcon href="https://linkedin.com/in/richardplotka" target="_blank" rel="noopener noreferrer">
                  <FiLinkedin size={24} />
                </SocialIcon>
              </SocialIcons>
            </HeroContent>
            
            <HeroImageContainer
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <HeroImage src="/RMP-Headshot25.jpg" alt="Richard Plotka" />
            </HeroImageContainer>
          </HeroGrid>
        </Container>
      </HeroSection>

      {/* Blog Snippets Section */}
      {blogPosts.length > 0 && (
        <BlogSection>
          <Container>
            <SectionHeader>
              <SectionHeaderContent>
                <SectionTitle>Latest Insights</SectionTitle>
                <SectionSubtitle>Thoughts on technology, content management, and innovation</SectionSubtitle>
              </SectionHeaderContent>
              <ViewAllLink to="/blog">
                View All <FiArrowRight />
              </ViewAllLink>
            </SectionHeader>
            {shouldRotate && (
              <CarouselIndicator>Auto-rotating • {blogPosts.length} articles</CarouselIndicator>
            )}
            
            <CarouselWrapper>
              <ContentGrid>
                <AnimatePresence mode="popLayout">
                  {visibleBlogPosts.map((post, index) => (
                    <motion.div
                      key={`${post._id}-${currentIndex}-${index}`}
                      layout
                      initial={{ opacity: 0, y: index < 3 ? -100 : 100 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: index < 3 ? -100 : 100 }}
                      transition={{ 
                        duration: 0.6,
                        delay: isAnimating ? index * 0.05 : 0,
                        layout: { duration: 0.6 }
                      }}
                    >
                      <BlogCardWrapper>
                        {isAdmin && (
                          <CardAdminControls>
                            <EditIconButton onClick={(e) => {
                              e.preventDefault();
                              startBlogEdit(post);
                            }}>
                              <FiEdit2 />
                            </EditIconButton>
                            <DeleteIconButton onClick={(e) => {
                              e.preventDefault();
                              confirmBlogDelete(post);
                            }}>
                              <FiTrash2 />
                            </DeleteIconButton>
                          </CardAdminControls>
                        )}
                        <ContentCard
                          to={`/blog/${post._id}`}
                          category={post.category}
                          title={post.title}
                          excerpt={post.data?.excerpt || post.shortDescription}
                          status={isAdmin ? {
                            published: post.metadata?.published !== false,
                            highlighted: true  // All posts on home are highlighted by definition
                          } : undefined}
                          onTogglePublish={isAdmin ? () => handleTogglePublish(post) : undefined}
                          onToggleHighlight={isAdmin ? () => handleToggleHighlight(post) : undefined}
                          metadata={{
                            readTime: post.data?.readTime || 3,
                            tags: post.data?.tags || []
                          }}
                          index={0}
                        />
                      </BlogCardWrapper>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </ContentGrid>
            </CarouselWrapper>
          </Container>
        </BlogSection>
      )}

      {/* News Ticker Section */}
      <NewsTickerSection>
        <NewsTicker />
      </NewsTickerSection>

      {/* Hero Edit Modal */}
      <EditableModal
        isOpen={showHeroModal}
        isCreating={false}
        onClose={cancelHeroEdit}
        onSave={saveHero}
        title="Edit Hero Content"
      >
        <label>Greeting</label>
        <input
          type="text"
          value={heroFormData.data?.greeting || ''}
          onChange={e => updateHeroFormData({
            data: { ...heroFormData.data, greeting: e.target.value }
          })}
          placeholder="e.g., Hello, I'm"
        />

        <label>Name</label>
        <input
          type="text"
          value={heroFormData.title || ''}
          onChange={e => updateHeroFormData({ title: e.target.value })}
          placeholder="e.g., Richard Plotka"
        />

        <label>Title/Role</label>
        <input
          type="text"
          value={heroFormData.subtitle || ''}
          onChange={e => updateHeroFormData({ subtitle: e.target.value })}
          placeholder="e.g., Full-Stack Developer & Architect"
        />

        <label>Description</label>
        <RichTextEditor
          value={heroFormData.description || ''}
          onChange={(content) => updateHeroFormData({ description: content })}
          placeholder="Brief introduction about yourself..."
          height="150px"
        />

        <label>Primary Button Text</label>
        <input
          type="text"
          value={heroFormData.data?.cta1Text || ''}
          onChange={e => updateHeroFormData({
            data: { ...heroFormData.data, cta1Text: e.target.value }
          })}
          placeholder="e.g., View My Work"
        />

        <label>Primary Button Link</label>
        <input
          type="text"
          value={heroFormData.data?.cta1Link || ''}
          onChange={e => updateHeroFormData({
            data: { ...heroFormData.data, cta1Link: e.target.value }
          })}
          placeholder="e.g., /portfolio"
        />

        <label>Secondary Button Text</label>
        <input
          type="text"
          value={heroFormData.data?.cta2Text || ''}
          onChange={e => updateHeroFormData({
            data: { ...heroFormData.data, cta2Text: e.target.value }
          })}
          placeholder="e.g., Get In Touch"
        />

        <label>Secondary Button Link</label>
        <input
          type="text"
          value={heroFormData.data?.cta2Link || ''}
          onChange={e => updateHeroFormData({
            data: { ...heroFormData.data, cta2Link: e.target.value }
          })}
          placeholder="e.g., /contact"
        />
      </EditableModal>

      {/* Blog Edit Modal */}
      <BlogEditModal
        isOpen={showBlogModal}
        isCreating={isCreatingBlog}
        onClose={cancelBlogEdit}
        onSave={saveBlog}
        formData={blogFormData}
        updateFormData={updateBlogFormData}
      />

      {/* Blog Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={blogConfirmOpen}
        onClose={() => setBlogConfirmOpen(false)}
        onConfirm={deleteBlogItem}
        title="Delete Blog Post"
        message={`Are you sure you want to delete "${blogItemToDelete?.title}"? This action cannot be undone.`}
      />
    </HomeContainer>
    </>
  );
};

const HomeContainer = styled.div`
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const LoadingText = styled.p`
  font-size: 1.25rem;
  color: var(--text-secondary);
  text-align: center;
  padding: 4rem 0;
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 4rem 2rem;
`;

const ErrorTitle = styled.h2`
  font-size: 2rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const ErrorText = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
`;

const RetryButton = styled.button`
  padding: 0.75rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const EditButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const HeroSection = styled.section`
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  padding: 4rem 0;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const HeroContent = styled.div`
  position: relative;
  max-width: 800px;

  @media (max-width: 968px) {
    order: 2;
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  justify-self: center;

  @media (max-width: 968px) {
    order: 1;
    max-width: 400px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: 20px;
    bottom: 20px;
    background: var(--accent-gradient);
    border-radius: 20px;
    z-index: -1;
    opacity: 0.5;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 20px;
  box-shadow: var(--shadow-xl);
  object-fit: cover;
`;

const WeatherWidgetPosition = styled.div`
  position: fixed;
  top: 80px;
  left: 2rem;
  z-index: 100;
  width: 240px;

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    max-width: 240px;
    margin: 1rem auto;
  }
`;

const Greeting = styled.p`
  color: var(--accent-primary);
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const Name = styled.h1`
  font-size: clamp(3rem, 8vw, 5rem);
  margin-bottom: 1rem;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Title = styled.h2`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 600px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  padding: 1rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const SecondaryButton = styled.button`
  padding: 1rem 2rem;
  background: transparent;
  color: var(--accent-primary);
  border: 2px solid var(--accent-primary);
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-primary);
    color: white;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--bg-secondary);
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-gradient);
    color: white;
    transform: translateY(-3px);
  }
`;

const BlogSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);
`;

const NewsTickerSection = styled.section`
  width: 100%;
  background: var(--bg-secondary);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const SectionHeaderContent = styled.div`
  flex: 1;
  text-align: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  margin-bottom: 0.5rem;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--accent);
  font-weight: 600;
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--accent);
  border-radius: 8px;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background: var(--accent);
    color: white;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const CarouselIndicator = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  opacity: 0.7;
  text-align: center;
`;

const CarouselWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const BlogCardWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const CardAdminControls = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  gap: 0.5rem;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${BlogCardWrapper}:hover & {
    opacity: 1;
  }
`;

const EditIconButton = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: var(--accent-secondary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DeleteIconButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export default Home;
